/**
 * Mobile-only Swiper for the `ax-text-icon-columns` section (columns layout).
 *
 * Desktop renders a CSS grid (SSR). On viewports < 750px the grid is hidden
 * and a Swiper is initialised that wraps each `.ax-text-icon-columns__col`
 * in a `<swiper-slide>`. The init is reversible: a `matchMedia` listener
 * destroys the Swiper and restores the grid when the viewport grows past
 * the breakpoint (handles iPad rotation / browser resize).
 *
 * Mirrors the canonical pattern of `details-reassurance-swiper.liquid`.
 *
 * @class
 * @extends HTMLElement
 */
class AxTextIconColumns extends HTMLElement {
	#selectors = {
		grid: '[ref="grid"]',
		swiper: '[ref="swiper"]',
		dots: '[ref="dots"]',
		dot: '.ax-text-icon-columns__dot',
		col: '.ax-text-icon-columns__col',
	};

	#classes = {
		ready: 'is-swiper-ready',
		activeDot: 'is-active',
	};

	/** @type {{ grid?: HTMLElement, swiper?: HTMLElement & { swiper?: any, initialize?: () => void }, dots?: HTMLElement }} */
	#elements = {};

	/** @type {MediaQueryList | null} */
	#mobileQuery = null;

	/** @type {any} Swiper instance */
	#swiper = null;

	/** @type {HTMLElement[]} Original column nodes — kept in memory so we can re-attach to the grid on destroy. */
	#originalCols = [];

	async connectedCallback() {
		this.#cacheElements();
		if (!this.#isValidSetup()) return;

		this.#mobileQuery = window.matchMedia('(max-width: 749px)');

		await customElements.whenDefined('swiper-container');

		this.#syncToBreakpoint();
		this.#mobileQuery.addEventListener('change', this.#onBreakpointChange);
	}

	disconnectedCallback() {
		this.#mobileQuery?.removeEventListener('change', this.#onBreakpointChange);
		this.#destroySwiper();
	}

	/**
	 * Cache DOM elements.
	 */
	#cacheElements() {
		this.#elements = {
			grid: /** @type {HTMLElement | undefined} */ (this.querySelector(this.#selectors.grid) ?? undefined),
			swiper:
				/** @type {(HTMLElement & { swiper?: any, initialize?: () => void }) | undefined} */ (
					this.querySelector(this.#selectors.swiper) ?? undefined
				),
			dots: /** @type {HTMLElement | undefined} */ (this.querySelector(this.#selectors.dots) ?? undefined),
		};
	}

	/**
	 * Validate required elements exist.
	 * @returns {boolean}
	 */
	#isValidSetup() {
		if (!this.#elements.grid || !this.#elements.swiper) return false;
		const cols = this.#elements.grid.querySelectorAll(this.#selectors.col);
		if (cols.length === 0) return false;
		this.#originalCols = [...cols];
		return true;
	}

	#onBreakpointChange = () => {
		this.#syncToBreakpoint();
	};

	/**
	 * Init Swiper when entering mobile, destroy when leaving.
	 * In `stacked` mode there is no slider at all — the columns stay in the
	 * grid and flow vertically via CSS, so Swiper is never initialised.
	 */
	#syncToBreakpoint() {
		if (this.dataset.mobileSliderType === 'stacked') return;

		if (this.#mobileQuery?.matches) {
			this.#initSwiper();
		} else {
			this.#destroySwiper();
		}
	}

	/**
	 * Initialise Swiper and migrate columns into slides.
	 * Slider type (`dots` vs `peek`) is read from the host's data attribute
	 * and configures `slides-per-view` accordingly. In peek mode the CSS
	 * handles the slide width via `::part(container) { overflow: visible }`
	 * and per-slide flex sizing.
	 */
	#initSwiper() {
		if (this.#swiper) return;
		const { swiper, grid } = this.#elements;
		if (!swiper || !grid || this.#originalCols.length === 0) return;

		const isPeek = this.dataset.mobileSliderType === 'peek';

		for (const col of this.#originalCols) {
			const slide = document.createElement('swiper-slide');
			slide.appendChild(col);
			swiper.appendChild(slide);
		}

		// 1.4 slides per view in peek mode = slide width is container/1.4,
		// so ~29% of the next slide stays visible on the trailing edge.
		// Swiper computes widths itself — avoids the CSS % chicken-and-egg
		// problem caused by `slides-per-view: auto` + width%.
		// `space-between` mirrors the section's mobile column gap setting.
		const peekGap = Number(this.dataset.gapMobile) || 24;
		swiper.setAttribute('slides-per-view', isPeek ? '1.4' : '1');
		swiper.setAttribute('space-between', isPeek ? String(peekGap) : '0');

		this.classList.add(this.#classes.ready);
		swiper.initialize?.();
		swiper.removeAttribute('init');
		this.#swiper = swiper.swiper ?? null;

		grid.hidden = true;

		this.#bindDots();
		this.#swiper?.on('slideChange', this.#updateDots);
		this.#updateDots();
	}

	/**
	 * Destroy Swiper and restore the original grid layout.
	 */
	#destroySwiper() {
		if (!this.#swiper) {
			this.classList.remove(this.#classes.ready);
			return;
		}

		this.#swiper.off('slideChange', this.#updateDots);

		try {
			this.#swiper.destroy(true, true);
		} catch {
			/* noop — Swiper sometimes throws when destroyed mid-transition */
		}
		this.#swiper = null;

		const { swiper, grid } = this.#elements;
		if (grid) {
			for (const col of this.#originalCols) {
				grid.appendChild(col);
			}
			grid.hidden = false;
		}

		if (swiper) {
			while (swiper.firstChild) swiper.removeChild(swiper.firstChild);
			swiper.setAttribute('init', 'false');
		}

		this.classList.remove(this.#classes.ready);
		this.#resetDotsActive();
	}

	/**
	 * Wire dot buttons to slideToLoop.
	 */
	#bindDots() {
		const dotsEl = this.#elements.dots;
		if (!dotsEl) return;
		const dots = dotsEl.querySelectorAll(this.#selectors.dot);
		for (const dot of dots) {
			dot.addEventListener('click', this.#onDotClick);
		}
	}

	#onDotClick = (event) => {
		const target = event.currentTarget;
		if (!(target instanceof HTMLElement)) return;
		const index = Number(target.dataset.slideIndex) || 0;
		this.#swiper?.slideTo(index);
	};

	/**
	 * Sync `.is-active` on the dot matching the current slide.
	 */
	#updateDots = () => {
		const dotsEl = this.#elements.dots;
		if (!dotsEl || !this.#swiper) return;
		const activeIndex = this.#swiper.activeIndex ?? 0;
		const dots = dotsEl.querySelectorAll(this.#selectors.dot);
		for (const dot of dots) dot.classList.remove(this.#classes.activeDot);
		dots[activeIndex]?.classList.add(this.#classes.activeDot);
	};

	/**
	 * Reset all dots back to "first active" when Swiper is torn down.
	 */
	#resetDotsActive() {
		const dotsEl = this.#elements.dots;
		if (!dotsEl) return;
		const dots = dotsEl.querySelectorAll(this.#selectors.dot);
		for (const dot of dots) dot.classList.remove(this.#classes.activeDot);
		dots[0]?.classList.add(this.#classes.activeDot);
	}
}

if (!customElements.get('ax-text-icon-columns')) {
	customElements.define('ax-text-icon-columns', AxTextIconColumns);
}
